import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../../api.ts';
import { TopBar } from '../../components/TopBar.tsx';
import { BottomNav } from '../../components/BottomNav.tsx';
import { FeedbackSummary } from '../../components/FeedbackSummary.tsx';
import { ReliabilityBadges } from '../../components/ReliabilityBadges.tsx';
import { INCIDENT_LABELS, type Feedback, type IncidentView, type Reliability } from '../../ratings.ts';

interface Data extends Feedback {
  incidents: IncidentView[];
  confirmedIncidents: { noShow: number; earlyDeparture: number };
  reliability: Reliability;
}

const STATUS = {
  OPEN: { label: 'En cours d’examen', badge: 'badge-pending' },
  CONFIRMED: { label: 'Confirmé par l’équipe', badge: 'badge-unavailable' },
  DISMISSED: { label: 'Classé sans suite', badge: 'badge-available' },
} as const;

const fmt = (iso: string) => new Date(iso).toLocaleDateString('fr-FR');

function IncidentCard({ incident, onChanged }: { incident: IncidentView; onChanged: () => void }) {
  const [text, setText] = useState(incident.response ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function respond() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/feedback/incidents/${incident.id}/respond`, { response: text });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'INVALID_RESPONSE' ? 'Écrivez au moins quelques mots.' : 'Envoi impossible.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 800 }}>{INCIDENT_LABELS[incident.kind]}</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{incident.missionTitle} · {incident.organizationName} · {fmt(incident.missionStartsAt)}</div>
        </div>
        <span className={`badge ${STATUS[incident.status].badge}`}>{STATUS[incident.status].label}</span>
      </div>
      {incident.note && <div style={{ fontSize: 13, lineHeight: 1.5 }}><strong>Signalement :</strong> {incident.note}</div>}
      {incident.status === 'OPEN' ? (
        <>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>Rien n'est décidé. Donnez votre version : elle sera lue avant toute décision.</div>
          {error && <div className="error-banner">{error}</div>}
          <textarea className="input" rows={3} maxLength={1000} placeholder="Votre version des faits" value={text} onChange={(e) => setText(e.target.value)} aria-label="Votre réponse" />
          <button className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-start' }} disabled={busy || text.trim().length < 3} onClick={respond}>{incident.response ? 'Mettre à jour ma réponse' : 'Envoyer ma réponse'}</button>
        </>
      ) : (
        <>
          {incident.response && <div style={{ fontSize: 13, lineHeight: 1.5 }}><strong>Votre réponse :</strong> {incident.response}</div>}
          {incident.decisionNote && <div style={{ fontSize: 13, lineHeight: 1.5 }}><strong>Décision :</strong> {incident.decisionNote}</div>}
        </>
      )}
    </div>
  );
}

export function ExtraFeedback() {
  const [data, setData] = useState<Data | null>(null);
  const load = useCallback(() => {
    api.get<Data>('/feedback/extra').then(setData);
  }, []);
  useEffect(load, [load]);

  return (
    <div className="app-shell">
      <TopBar title="Mes avis" back />
      <div className="screen-content">
        {!data && <div className="empty-state">Chargement…</div>}
        {data && (
          <>
            <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontWeight: 800 }}>Ma fiabilité</div>
              <ReliabilityBadges reliability={data.reliability} />
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                {data.reliability.honored} mission(s) menée(s) à bien · {data.reliability.lastMinuteCancels} désistement(s) de dernière minute · {data.reliability.noShows} absence(s) confirmée(s).
                {data.reliability.reliabilityPct === null && ' L’indice s’affichera après quelques missions.'}
              </div>
              {data.reliability.topExtra && <div className="badge badge-available" style={{ padding: '8px 12px' }}>Vous avez le badge « Top Extra ». Il se perd en cas de retard, d'absence ou de baisse de moyenne.</div>}
            </div>
            <div className="card"><FeedbackSummary feedback={data} /></div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, padding: '0 2px 8px' }}>Signalements</div>
              {data.incidents.length === 0 && <div className="empty-state">Aucun signalement.</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {data.incidents.map((i) => <IncidentCard key={i.id} incident={i} onChanged={load} />)}
              </div>
            </div>
          </>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
